import { Filesystem, Directory } from '@capacitor/filesystem';
import { isCapacitor } from '@/lib/constants';
import { fixDropboxUrl } from '@/lib/utils';

/**
 * Keeping meditations on the device.
 *
 * A meditation streams its voice and music tracks separately, tens of
 * megabytes each, every time somebody plays it. Downloading once turns a
 * repeat listen into no traffic at all -- which matters both for the hosting
 * bill and for anyone listening on mobile data or somewhere without a signal.
 *
 * Files go in Directory.Data, which on iOS is Library/NoCloud: downloadable
 * content must not be swept into a user's iCloud backup, and Apple rejects
 * apps that do that. Android maps it to internal app storage, so uninstalling
 * the app reclaims everything.
 *
 * Native only. The browser has no comparable place to put a 20 MB file that
 * the user can then manage, so on web every call reports "not downloaded" and
 * playback streams as before.
 */

const FOLDER = 'offline-audio';

export const offlineSupported = (): boolean => isCapacitor();

/** Stable filename for a remote URL: same track, same file, every time. */
function keyFor(url: string): string {
  const clean = fixDropboxUrl(url);
  let hash = 0;
  for (let i = 0; i < clean.length; i += 1) {
    hash = (hash << 5) - hash + clean.charCodeAt(i);
    hash |= 0;
  }
  const name = decodeURIComponent(clean.split('?')[0].split('/').pop() || 'audio');
  const safe = name.replace(/[^a-zA-Z0-9._-]/g, '-').slice(-60);
  return `${Math.abs(hash).toString(36)}-${safe}`;
}

/**
 * A file:// path can't be fed to an <audio> tag in a WKWebView; Capacitor
 * rewrites it to something the webview is allowed to load.
 */
function toPlayableSrc(uri: string): string {
  const cap = (window as unknown as { Capacitor?: { convertFileSrc?: (u: string) => string } })
    .Capacitor;
  return cap?.convertFileSrc ? cap.convertFileSrc(uri) : uri;
}

async function ensureFolder(): Promise<void> {
  try {
    await Filesystem.mkdir({ path: FOLDER, directory: Directory.Data, recursive: true });
  } catch {
    // already there
  }
}

/** Local playable URI for a track, or null when it isn't downloaded. */
export async function localUriFor(url: string | null | undefined): Promise<string | null> {
  if (!url || !offlineSupported()) return null;
  try {
    const { uri } = await Filesystem.getUri({
      path: `${FOLDER}/${keyFor(url)}`,
      directory: Directory.Data,
    });
    // getUri answers for paths that don't exist, so confirm with a stat.
    await Filesystem.stat({ path: `${FOLDER}/${keyFor(url)}`, directory: Directory.Data });
    return toPlayableSrc(uri);
  } catch {
    return null;
  }
}

export async function isDownloaded(url: string | null | undefined): Promise<boolean> {
  return (await localUriFor(url)) !== null;
}

/**
 * Downloads one track. Resolves to the local URI.
 *
 * Uses the plugin's own downloadFile rather than fetch + base64: these files
 * run to tens of megabytes, and routing them through a JS string would spike
 * memory enough to have the OS kill the app on an older phone.
 */
export async function downloadTrack(url: string): Promise<string> {
  if (!offlineSupported()) throw new Error('Downloaden kan alleen in de app.');
  await ensureFolder();
  const path = `${FOLDER}/${keyFor(url)}`;
  const { path: written } = await Filesystem.downloadFile({
    url: fixDropboxUrl(url),
    path,
    directory: Directory.Data,
  });
  if (!written) throw new Error('Download leverde geen bestand op.');
  const { uri } = await Filesystem.getUri({ path, directory: Directory.Data });
  return toPlayableSrc(uri);
}

export async function removeTrack(url: string): Promise<void> {
  if (!offlineSupported()) return;
  try {
    await Filesystem.deleteFile({ path: `${FOLDER}/${keyFor(url)}`, directory: Directory.Data });
  } catch {
    // wasn't there
  }
}

export interface StoredTrack {
  name: string;
  bytes: number;
}

/** Everything currently held, for the manage-downloads screen. */
export async function listStored(): Promise<StoredTrack[]> {
  if (!offlineSupported()) return [];
  try {
    const { files } = await Filesystem.readdir({ path: FOLDER, directory: Directory.Data });
    return files.map((f) => ({
      // strip the hash prefix that keeps names unique
      name: (f.name.split('-').slice(1).join('-') || f.name).replace(/\.mp3$/i, ''),
      bytes: f.size ?? 0,
    }));
  } catch {
    return [];
  }
}

export async function totalStoredBytes(): Promise<number> {
  return (await listStored()).reduce((sum, f) => sum + f.bytes, 0);
}

export async function removeAll(): Promise<void> {
  if (!offlineSupported()) return;
  try {
    await Filesystem.rmdir({ path: FOLDER, directory: Directory.Data, recursive: true });
  } catch {
    // nothing stored
  }
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} kB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
